import { type ForwardedRef, forwardRef, useMemo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import GroupFilter from '../../../../assets/frontend/js/components/GroupFilter'
import { CustomSearchFilter } from '../../../../assets/frontend/js/components/SearchFilter'
import type { View } from './SearchUI'
import LayoutToggle from './LayoutToggle'
import { getCurrentPosition } from '../utils/geoLocation'
import { configActions } from '../store/configSlice'
import { filterActions } from '../store/filterSlice'
import FilterToggle from '../../../../assets/frontend/js/components/FilterToggle'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { useDisplayController } from '../../../../assets/frontend/js/hooks/dom'
import FilterModal from '../../../../assets/frontend/js/components/FilterModal'
import TagFilter from '../../../../assets/frontend/js/components/TagFilter'
import { _x } from '@wordpress/i18n'

type FilterPanelProps = {
  view: View
  changeView: (view: View) => void
  collapsed: boolean
  modalId: string
}

const FilterPanel = forwardRef(function FilterPanel(
  { view, changeView, collapsed, modalId }: FilterPanelProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const dispatch = useAppDispatch()
  const locationTypes = useAppSelector((state) => state.data.locationTypes)

  const groupIcons = useMemo(
    () =>
      locationTypes.reduce(
        (acc, type) => {
          acc[type.slug] = type.icon
          return acc
        },
        {} as Record<string, string>
      ),
    [locationTypes]
  )

  const labels = useMemo(
    () =>
      locationTypes.reduce(
        (acc, type) => {
          acc[type.slug] = type.label
          return acc
        },
        {} as Record<string, string>
      ),
    [locationTypes]
  )

  const onLocationButtonClick = async () => {
    const { coords } = await getCurrentPosition()
    dispatch(
      configActions.setGeoLocation({
        lat: coords.latitude,
        lng: coords.longitude
      })
    )
    dispatch(
      filterActions.setLocation({ lat: coords.latitude, lng: coords.longitude })
    )
  }

  const filterBarRef = useRef<HTMLDivElement>(null)

  const preFilterLocationType = useAppSelector(
    (state) => state.filter.preFilterLocationType
  )

  useDisplayController(filterBarRef)

  return (
    <>
      <div className="filter-panel" data-bs-theme="dark">
        <div>
          <CustomSearchFilter
            placeholder={_x(
              'Which location are you looking for?',
              'Location Finder Search Filter Placeholder',
              'icf'
            )}
            renderBefore={
              <button
                type="button"
                className="search-filter-button"
                onClick={onLocationButtonClick}
              >
                <i className="icon fa-location-crosshairs" />
              </button>
            }
          />
        </div>
        <div
          ref={filterBarRef}
          className={classNames('filter-bar', {
            show: !collapsed,
            'hidden-on-mobile': !!preFilterLocationType
          })}
        >
          <div className="filter-bar__content">
            {!preFilterLocationType && (
              <GroupFilter
                labels={labels}
                attribute="status"
                icons={groupIcons}
                ariaLabel="Filter by type"
              />
            )}
            <div className="filter-panel-button-group d-none d-md-flex ms-auto">
              <FilterToggle variant="desktop" modalId={modalId} />
              <LayoutToggle
                variant="desktop"
                value={view}
                onChange={changeView}
              />
            </div>
          </div>
        </div>
      </div>

      {createPortal(
        <FilterModal
          title={_x('Filter Locations', 'Filter Modal Title', 'icf')}
          id={modalId}
        >
          <div className="filter-modal-filter-section">
            <TagFilter
              isTranslatedAttribute
              attribute="country.name"
              limit={5}
              title={_x('Country', 'Filter Modal Filter Label', 'icf')}
              ariaLabel={_x(
                'Filter by country',
                'Filter Modal Filter ARIA Label',
                'icf'
              )}
            />
          </div>
        </FilterModal>,
        document.body
      )}
    </>
  )
})

export default FilterPanel
